// NEW_FILE_JUSTIFIED: New messages feature component
import { memo, useState, useRef, useCallback } from 'react';
import { Send, Paperclip, Smile, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { MESSAGE_CHANNELS } from '@/constants/messagesMockData';
import { ReplyPreview } from '@/components/messages/ReplyPreview';
import type { IMessageComposerProps } from '@/@types/messages';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_FILE_TYPES = 'image/*,.pdf,.doc,.docx,.xls,.xlsx';

export const MessageComposer = memo(function MessageComposer({
  onSendMessage,
  onSendFile,
  isSendingFile,
  activeChannel,
  onChannelChange,
  replyContext,
  onCancelReply,
}: IMessageComposerProps) {
  const [messageText, setMessageText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const MAX_HEIGHT = 120;
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessageText(e.target.value);
      resizeTextarea();
    },
    [resizeTextarea]
  );

  const handleSend = useCallback(() => {
    const trimmed = messageText.trim();
    if (!trimmed) return;
    onSendMessage(trimmed, replyContext?.messageId);
    setMessageText('');
    onCancelReply?.(); // Clear reply context after sending
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
    }
  }, [messageText, onSendMessage, replyContext, onCancelReply]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFileSelect = useCallback(() => {
    if (isSendingFile) return;
    fileInputRef.current?.click();
  }, [isSendingFile]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset input to allow re-selecting same file
      e.target.value = '';

      if (file.size > MAX_FILE_SIZE) {
        toast.error('File is too large. Maximum size is 50MB.');
        return;
      }

      // Use current text as caption, then clear it
      const caption = messageText.trim();
      onSendFile(file, caption);
      setMessageText('');
      const el = textareaRef.current;
      if (el) {
        el.style.height = 'auto';
      }
    },
    [messageText, onSendFile]
  );

  return (
    <div className="flex flex-col gap-3 border-t border-msg-panel-border bg-msg-panel-bg p-4">
      {/* Reply preview */}
      {replyContext && onCancelReply && (
        <ReplyPreview replyContext={replyContext} onCancel={onCancelReply} />
      )}
      {/* Channel tabs */}
      <div role="tablist" aria-label="Send via channel" className="flex w-full">
        {MESSAGE_CHANNELS.map(channel => {
          const isActive = channel.value === activeChannel;
          return (
            <button
              key={channel.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChannelChange(channel.value)}
              className={cn(
                'px-4 py-2 text-[13px] transition-colors',
                isActive
                  ? 'rounded-t-lg bg-msg-channel-tab-active font-semibold text-foreground'
                  : 'font-normal text-muted-foreground hover:text-foreground'
              )}
            >
              {channel.label}
            </button>
          );
        })}
      </div>

      {/* Input row */}
      <div className="flex items-end gap-3">
        {/* Textarea wrapper */}
        <div className="flex-1 rounded-xl border border-msg-composer-input-border bg-msg-composer-input-bg p-3">
          <label htmlFor="message-composer-input" className="sr-only">
            Type your message
          </label>
          <textarea
            id="message-composer-input"
            ref={textareaRef}
            value={messageText}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            rows={1}
            className={cn(
              'w-full resize-none bg-transparent text-[14px] text-foreground outline-none placeholder:text-muted-foreground',
              'min-h-[40px] max-h-[120px]'
            )}
          />
          {/* Action icons row */}
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleFileSelect}
                disabled={isSendingFile}
                className={cn(
                  'rounded text-muted-foreground transition-colors hover:text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                  isSendingFile && 'cursor-not-allowed opacity-50'
                )}
                aria-label={isSendingFile ? 'Sending file...' : 'Attach file'}
              >
                {isSendingFile ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Paperclip size={16} />
                )}
              </button>
              <button
                type="button"
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Insert emoji"
              >
                <Smile size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          onChange={handleFileChange}
          className="sr-only"
          tabIndex={-1}
        />

        {/* Send button */}
        <button
          type="button"
          onClick={handleSend}
          aria-label="Send message"
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-primary',
            'bg-primary-foreground transition-opacity hover:opacity-80'
          )}
        >
          <Send size={18} className="text-primary" />
        </button>
      </div>
    </div>
  );
});
